import { useEffect } from "react";
import type { ElementType } from "react";
import { RouterProvider } from "react-router-dom";
import ReduxToastr from "react-redux-toastr";
import { SnackbarProvider } from "notistack";

import "./App.scss";
import { router } from "./helpers/routes/routeConfig";
import {
  GlobalContextProvider,
  LoaderProvider,
} from "./helpers/contexts/contextProviders";
import MainSEO from "./components/seo/MainSEO";
import ErrorBoundary from "./components/ErrorBoundary";
import {
  createBroadcastChannel,
  closeBroadcastChannel,
} from "@/shared/helpers/broadcastChannelHelper";
import { logger } from "./helpers/loggerHelper";
import { TIMEOUTS } from "./config/constants";

const MainSEOComponent = MainSEO as ElementType;

type BroadcastChannelLike = {
  addEventListener: (
    _type: "message",
    _listener: (_event: MessageEvent) => void
  ) => void;
  removeEventListener: (
    _type: "message",
    _listener: (_event: MessageEvent) => void
  ) => void;
};

function App() {
  useEffect(() => {
    // Create a new Broadcast Channel
    const channel = createBroadcastChannel() as BroadcastChannelLike | null;

    if (!channel) return;

    // Stable message handler reference so it can be removed properly
    const handleBroadcastMessage = (event: MessageEvent) => {
      if (event?.data === "reload") {
        if (document.visibilityState === "hidden") {
          window.location.reload();
        }
      }
    };

    // Listen for messages on the channel
    channel.addEventListener("message", handleBroadcastMessage);

    return () => {
      try {
        channel.removeEventListener("message", handleBroadcastMessage);
      } catch (err) {
        logger.warn("Failed to remove broadcast listener during cleanup", err);
      }

      try {
        closeBroadcastChannel();
      } catch (err) {
        logger.warn("Failed to close broadcast channel during cleanup", err);
      }
    };
  }, []);

  return (
    <div className="App">
      <MainSEOComponent />
      <LoaderProvider>
        <GlobalContextProvider>
          <SnackbarProvider
            autoHideDuration={TIMEOUTS.SNACKBAR_DURATION}
            preventDuplicate
            anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
          >
            <ErrorBoundary>
              <RouterProvider router={router} />
            </ErrorBoundary>
          </SnackbarProvider>
          <ReduxToastr
            timeOut={TIMEOUTS.TOAST_DURATION}
            newestOnTop={false}
            preventDuplicates
            position="top-right"
            getState={(state: { toastr: unknown }) => state.toastr}
            transitionIn="fadeIn"
            transitionOut="fadeOut"
            progressBar
            closeOnToastrClick
          />
        </GlobalContextProvider>
      </LoaderProvider>
    </div>
  );
}

export default App;
